import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader, Button, Card, CardHeader, CardTitle, CardContent, EmptyState } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { getBlogPostList, deleteBlogPost } from '@/services/blogPostService';
import { formatDateShort } from '@/utils/dateUtils';
import type { BlogPostListItemDto } from '@/types/api/blog-post';
import { useApiLanguage } from '@/hooks/useApiLanguage';

const PAGE_SIZE = 10;

/**
 * 블로그 목록 페이지.
 * GNB "블로그" 메뉴 클릭 시 노출.
 */
export default function BlogList(): React.ReactElement {
  const { t, i18n } = useTranslation('menu');
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const queryClient = useQueryClient();
  const lang = useApiLanguage();
  const [page, setPage] = useState(1);
  const [allPosts, setAllPosts] = useState<BlogPostListItemDto[]>([]);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['blogPostList', page, lang, isAdmin],
    queryFn: () =>
      getBlogPostList({ page, pageSize: PAGE_SIZE, language: lang }, { skipAuth: !isAdmin }),
    staleTime: 0,
  });

  useEffect(() => {
    setPage(1);
    setAllPosts([]);
  }, [location.key]);

  useEffect(() => {
    if (data?.blogPosts) {
      if (page === 1) {
        setAllPosts(data.blogPosts);
      } else {
        setAllPosts(prev => [...prev, ...data.blogPosts]);
      }
    }
  }, [data, page]);

  const posts = allPosts;
  const total = data?.total ?? 0;
  const hasMore = page * PAGE_SIZE < total;

  const postUrl = (id: number) => (isAdmin ? ROUTES.ADMIN.BLOG_POST(String(id)) : ROUTES.COMMON.BLOG_POST(String(id)));

  const loadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: '100px', threshold: 0 }
    );
    const el = sentinelRef.current;
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const handleDelete = async (postId: number) => {
    if (!window.confirm(t('blogPage.deleteConfirm'))) return;
    try {
      await deleteBlogPost(postId);
      queryClient.invalidateQueries({ queryKey: ['blogPostList'] });
    } catch {
      alert(t('blogPage.deleteError'));
    }
  };

  const getThumbnail = (thumbnailImageUrl: BlogPostListItemDto['thumbnailImageUrl']): string => {
    if (typeof thumbnailImageUrl === 'string') return thumbnailImageUrl;
    return '';
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 py-8">
      <PageHeader
        title={t('blog')}
        description={t('blogPage.description')}
        rightContent={
          isAdmin ? (
            <Link to={ROUTES.ADMIN.BLOG_CREATE}>
              <Button variant="lightdark" size="md">
                <Plus size={16} className="mr-2" />
                {t('blogPage.createButton')}
              </Button>
            </Link>
          ) : null
        }
      />

      <div className="mt-8">
        {isLoading ? (
          <p className="text-gray-500 text-sm">{t('blogPage.loading')}</p>
        ) : posts.length === 0 ? (
          <EmptyState
            title={t('blogPage.emptyTitle')}
            description={t('blogPage.emptyDescription')}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => {
                const thumb = getThumbnail(post.thumbnailImageUrl);
                return (
                  <Card key={post.id} className="flex flex-col h-full overflow-hidden hover:shadow-md transition-shadow">
                    <Link to={postUrl(post.id)} className="flex flex-col flex-1">
                      <div className="aspect-video w-full bg-gray-100 shrink-0 overflow-hidden rounded-t-lg">
                        {thumb ? (
                          <img src={thumb} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full bg-gray-200" aria-hidden />
                        )}
                      </div>
                      <CardHeader className="pb-2">
                        <p className="text-xs text-gray-500 mb-1">{formatDateShort(post.publishedAt, i18n.language)}</p>
                        <CardTitle className="text-lg">{post.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 mt-auto">
                        <span className="text-sm font-medium text-brand-600 hover:text-brand-700">
                          {t('blogPage.readMore')} →
                        </span>
                      </CardContent>
                    </Link>
                    {isAdmin && (
                      <CardContent className="pt-0 pb-4 flex gap-2">
                        <Link to={ROUTES.ADMIN.BLOG_EDIT(String(post.id))}>
                          <Button variant="outline" size="sm">
                            <Pencil size={14} className="mr-1.5 shrink-0" />
                            {t('blogPage.editButton')}
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(post.id)}
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                          <Trash2 size={14} className="mr-1.5 shrink-0" />
                          {t('blogPage.delete')}
                        </Button>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
            <div ref={sentinelRef} className="min-h-[1px] mt-2" aria-hidden />
          </>
        )}
      </div>
    </div>
  );
}
